import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Movie, Showtime } from '../../models';
import { MovieService } from '../../services/movie.service';
import { CompactMovieRowComponent } from '../../components/compact-movie-row.component';
import { ShowtimeService } from '../../services/showtime.service';
import { LocationFilterService } from '../../services/location-filter.service';

@Component({
  selector: 'app-movies',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, CompactMovieRowComponent],
  template: `
    <section class="listing-shell">
      <div class="container listing-top">
        <div class="listing-head">
          <div>
            <h1>Movies</h1>
            <p>{{ movies.length }} live movie{{ movies.length === 1 ? '' : 's' }}</p>
          </div>
          <a class="mini-link" routerLink="/">Showtimes</a>
        </div>

        <div class="glass listing-search">
          <input class="input search-input" [(ngModel)]="query" placeholder="Search movie title" (ngModelChange)="scheduleQueryUpdate()" (keyup.enter)="updateQueryParams()" />
          <div class="filter-line">
            <select [(ngModel)]="county" (ngModelChange)="updateQueryParams()"><option value="">All counties</option><option *ngFor="let county of counties">{{ county }}</option></select>
            <select [(ngModel)]="language" (ngModelChange)="updateQueryParams()"><option value="">All languages</option><option *ngFor="let language of languages">{{ language }}</option></select>
            <input class="input" [(ngModel)]="date" type="date" aria-label="Date" (ngModelChange)="updateQueryParams()" />
            <button class="clear-btn" type="button" (click)="clearFilters()">Clear</button>
          </div>
        </div>
      </div>
    </section>

    <section class="container listing-results">
      <div *ngIf="error" class="error-state">{{ error }}</div>
      <div *ngIf="loading" class="list-skeleton"><div *ngFor="let item of skeletons"></div></div>

      <div class="result-head" *ngIf="!loading">
        <h2>{{ query || county || language || date ? 'Matching movies' : 'Now showing' }}</h2>
        <span>{{ movies.length }} found</span>
      </div>

      <div class="compact-list" *ngIf="!loading && movies.length">
        <app-compact-movie-row *ngFor="let movie of movies" [movie]="movie" />
      </div>

      <div *ngIf="!loading && !movies.length && !error" class="empty-state">
        No movies found. Try clearing filters or choosing a different date.
      </div>
    </section>
  `,
  styles: [
    `
      .listing-shell { position: sticky; top: 68px; z-index: 20; padding: 14px 0 8px; background: linear-gradient(180deg, var(--bg) 0%, rgba(7, 9, 20, .94) 72%, transparent 100%); backdrop-filter: blur(14px); }
      .listing-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 12px; margin-bottom: 9px; }
      .listing-head h1 { margin: 0 0 2px; font-size: clamp(25px, 4vw, 38px); line-height: 1; }
      .listing-head p { margin: 0; color: var(--muted); font-size: 13px; font-weight: 850; }
      .mini-link { min-height: 36px; border: 1px solid var(--line); border-radius: 999px; padding: 8px 12px; color: var(--cyan); background: var(--control-bg); font-size: 13px; font-weight: 900; }
      .listing-search { display: grid; grid-template-columns: minmax(260px, 1fr) minmax(460px, 1.1fr); gap: 10px; padding: 10px; border-radius: 18px; box-shadow: 0 12px 34px rgba(0,0,0,.22); }
      .listing-search select { min-height: 46px; border: 1px solid var(--line); border-radius: 999px; padding: 0 14px; color: var(--text); background: var(--field-bg); }
      .search-input { font-weight: 850; }
      .filter-line { display: grid; grid-template-columns: minmax(130px, 1fr) minmax(130px, 1fr) minmax(136px, 1fr) auto; gap: 8px; }
      .clear-btn { min-height: 46px; border: 1px solid var(--line); border-radius: 999px; padding: 0 14px; color: var(--text); background: var(--control-bg); font-weight: 900; cursor: pointer; }
      .listing-results { padding: 8px 0 64px; }
      .result-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin: 6px 0 9px; }
      .result-head h2 { margin: 0; font-size: 18px; line-height: 1.1; }
      .result-head span { color: var(--muted); font-size: 12px; font-weight: 900; }
      .compact-list { display: grid; gap: 8px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .list-skeleton { display: grid; gap: 8px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .list-skeleton div { min-height: 74px; border-radius: 16px; background: var(--skeleton); background-size: 220% 100%; animation: shimmer 1.25s linear infinite; }
      @media (max-width: 1024px) {
        .listing-shell { top: 64px; }
        .listing-search { grid-template-columns: 1fr; }
      }
      @media (max-width: 700px) {
        .listing-shell { padding: 10px 0 8px; }
        .listing-head h1 { font-size: 24px; }
        .listing-head p { font-size: 12px; }
        .mini-link { display: none; }
        .listing-search { padding: 8px; gap: 8px; border-radius: 16px; }
        .filter-line { grid-template-columns: 1fr 1fr; }
        .compact-list, .list-skeleton { grid-template-columns: 1fr; }
      }
      @media (max-width: 380px) {
        .filter-line { grid-template-columns: 1fr; }
      }
    `,
  ],
})
export class MoviesComponent implements OnInit {
  query = '';
  county = '';
  language = '';
  date = '';
  movies: Movie[] = [];
  private rawMovies: Movie[] = [];
  private rawShowtimes: Showtime[] = [];
  languages: string[] = [];
  loading = true;
  error = '';
  skeletons = Array.from({ length: 8 });
  private queryTimer?: ReturnType<typeof setTimeout>;
  counties = ['Antrim', 'Armagh', 'Cavan', 'Clare', 'Cork', 'Derry', 'Donegal', 'Down', 'Dublin', 'Fermanagh', 'Galway', 'Kerry', 'Kildare', 'Laois', 'Limerick', 'Louth', 'Mayo', 'Meath', 'Monaghan', 'Offaly', 'Roscommon', 'Sligo', 'Tipperary', 'Tyrone', 'Waterford', 'Westmeath', 'Wexford', 'Wicklow'];
  constructor(
    private readonly moviesService: MovieService,
    private readonly showtimeService: ShowtimeService,
    private readonly location: LocationFilterService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {}
  ngOnInit() {
    this.loadLanguages();
    this.location.state$.subscribe(() => {
      if (this.location.state.mode !== 'all') {
        this.load();
      } else {
        this.applyLocationFilter();
      }
    });
    this.route.queryParamMap.subscribe((params) => {
      this.query = params.get('query') ?? '';
      this.county = params.get('county') ?? '';
      this.language = params.get('language') ?? '';
      this.date = params.get('date') ?? '';
      this.load();
    });
  }
  load() {
    this.loading = true;
    this.error = '';
    const county = this.location.state.mode !== 'all' ? '' : this.county;
    if (this.location.state.mode !== 'all') {
      this.showtimeService.list({ movie: this.query, county, date: this.date, language: this.language }).subscribe({
        next: (showtimes) => {
          this.rawShowtimes = showtimes;
          this.applyLocationFilter();
          this.loading = false;
        },
        error: () => { this.error = 'Could not load movies. Make sure the backend is running.'; this.loading = false; },
      });
      return;
    }

    this.moviesService.list({ query: this.query, county, date: this.date, language: this.language }).subscribe({
      next: (movies) => {
        this.rawMovies = movies;
        this.applyLocationFilter();
        this.loading = false;
      },
      error: () => { this.error = 'Could not load movies. Make sure the backend is running.'; this.loading = false; },
    });
  }
  scheduleQueryUpdate() {
    if (this.queryTimer) clearTimeout(this.queryTimer);
    this.queryTimer = setTimeout(() => this.updateQueryParams(), 350);
  }
  updateQueryParams() {
    if (this.queryTimer) clearTimeout(this.queryTimer);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        query: this.query.trim() || null,
        county: this.county || null,
        language: this.language || null,
        date: this.date || null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
  clearFilters() {
    this.query = '';
    this.county = '';
    this.language = '';
    this.date = '';
    this.updateQueryParams();
  }
  loadLanguages() {
    this.moviesService.list().subscribe((movies) => {
      this.languages = Array.from(new Set(movies.map((movie) => movie.language).filter(Boolean) as string[])).sort();
    });
  }

  private applyLocationFilter() {
    if (this.location.state.mode !== 'all') {
      this.movies = this.uniqueMoviesFromShowtimes(this.location.filterCinemas(this.rawShowtimes));
      return;
    }
    this.movies = this.rawMovies;
  }

  private uniqueMoviesFromShowtimes(showtimes: Showtime[]): Movie[] {
    const map = new Map<string, Movie>();
    showtimes.forEach((showtime) => {
      if (showtime.movie) map.set(showtime.movie.id, showtime.movie);
    });
    return Array.from(map.values()).sort((a, b) => a.title.localeCompare(b.title));
  }
}
